import type { BookingStatus, PaymentStatus, VehicleStatus } from '@zuruflex/types';
import { Badge, type BadgeProps } from '../ui/badge.js';

type Variant = NonNullable<BadgeProps['variant']>;

/**
 * Status colours live here rather than being picked per screen, so a RENTED
 * vehicle looks identical in admin and on the landing page.
 *
 * The *label* is passed in already translated: the chips stay presentational
 * and do not reach for a translation hook of their own.
 */
const VEHICLE_VARIANT: Record<VehicleStatus, Variant> = {
  AVAILABLE: 'success',
  RENTED: 'info',
  MAINTENANCE: 'warning',
  RETIRED: 'default',
};

const BOOKING_VARIANT: Record<BookingStatus, Variant> = {
  PENDING: 'warning',
  // Owner has accepted; waiting on the customer to pay.
  AWAITING_PAYMENT: 'info',
  CONFIRMED: 'info',
  ACTIVE: 'success',
  COMPLETED: 'default',
  CANCELLED: 'danger',
};

const PAYMENT_VARIANT: Record<PaymentStatus, Variant> = {
  UNPAID: 'warning',
  AUTHORIZED: 'info',
  PAID: 'success',
  PARTIALLY_REFUNDED: 'warning',
  REFUNDED: 'default',
  FAILED: 'danger',
};

function titleCase(status: string): string {
  return status.charAt(0) + status.slice(1).toLowerCase().replace(/_/g, ' ');
}

export function VehicleStatusChip({ status, label }: { status: VehicleStatus; label?: string }) {
  return <Badge variant={VEHICLE_VARIANT[status]}>{label ?? titleCase(status)}</Badge>;
}

export function BookingStatusChip({ status, label }: { status: BookingStatus; label?: string }) {
  return <Badge variant={BOOKING_VARIANT[status]}>{label ?? titleCase(status)}</Badge>;
}

export function PaymentStatusChip({ status, label }: { status: PaymentStatus; label?: string }) {
  return <Badge variant={PAYMENT_VARIANT[status]}>{label ?? titleCase(status)}</Badge>;
}
